import { useEffect, useState } from "react";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSpinner,
    FaArrowLeft
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getMenuPermissions } from "../utils/auth";

import "./Announcements.css";

// Requires: npm install react-toastify

const API_URL = process.env.REACT_APP_API_URL;

const SEVERITY_OPTIONS = ["info", "warning", "critical"];


function Announcements() {

    // Mirrors require_menu_permission("announcements") on the backend.
    const perms = getMenuPermissions("announcements");

    const emptyForm = {
        id: "",
        message: "",
        severity: "info",
        is_active: true,
        expires_at: ""
    };

    const [announcements, setAnnouncements] = useState([]);

    // Inline form -- shown instead of the table.
    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [form, setForm] = useState(emptyForm);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        loadAnnouncements();
    }, []);

    const loadAnnouncements = async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/announcements`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            if (Array.isArray(data)) {

                setAnnouncements(data);

            } else if (data.status) {

                setAnnouncements(data.data || []);

            } else {

                setAnnouncements([]);

                toast.error(data.message || "Failed to load announcements");

            }

        } catch (err) {

            console.error(err);

            setAnnouncements([]);

            toast.error("Unable to load announcements. Please try again.");

        } finally {

            setIsLoading(false);

        }

    };

    const handleChange = (e) => {

        setForm({
            ...form,
            [e.target.name]: e.target.value
        });

    };

    const handleActiveToggle = () => {

        setForm({
            ...form,
            is_active: !form.is_active
        });

    };

    const openAdd = () => {

        setIsEdit(false);

        setForm(emptyForm);

        setShowForm(true);

    };

    const openEdit = (item) => {

        setIsEdit(true);

        setForm({
            ...item,
            // datetime-local input needs "YYYY-MM-DDTHH:mm"
            expires_at: item.expires_at
                ? item.expires_at.slice(0, 16)
                : ""
        });

        setShowForm(true);

    };

    const closeForm = () => {

        if (isSaving) return; // don't let them leave mid-save

        setShowForm(false);

        setForm(emptyForm);

    };

    const saveAnnouncement = async () => {

        if (!form.message || !form.severity) {

            toast.warn("Please fill all fields");
            return;

        }

        setIsSaving(true);

        try {

            const payload = {

                ...form,

                id: isEdit ? form.id : 0,

                expires_at: form.expires_at || null

            };

            const res = await fetch(`${API_URL}/portal/announcements`, {

                method: isEdit ? "PUT" : "POST",

                headers: {
                    "Content-Type": "application/json"
                },

                body: JSON.stringify(payload)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                setShowForm(false);

                setForm(emptyForm);

                toast.success(
                    isEdit
                        ? "Announcement updated successfully"
                        : "Announcement added successfully"
                );

                loadAnnouncements();

            } else {

                toast.error(data.message || "Failed to save announcement");

            }

        } catch (err) {

            console.error("Failed to save announcement:", err);

            toast.error("Something went wrong while saving the announcement");

        } finally {

            setIsSaving(false);

        }

    };

    const deleteAnnouncement = async (id) => {

        const ok = window.confirm(
            "Are you sure you want to delete this announcement?"
        );

        if (!ok) return;

        setDeletingId(id);

        try {

            const res = await fetch(`${API_URL}/portal/announcements/${id}`, {

                method: "DELETE"

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success("Announcement deleted successfully");

                loadAnnouncements();

            } else {

                toast.error(data.message || "Failed to delete announcement");

            }

        } catch (err) {

            console.error("Failed to delete announcement:", err);

            toast.error("Something went wrong while deleting the announcement");

        } finally {

            setDeletingId(null);

        }

    };

    const formatDate = (value) => {

        if (!value) return "—";

        const d = new Date(value);

        if (isNaN(d.getTime())) return "—";

        const pad = (n) => String(n).padStart(2, "0");

        const day = pad(d.getDate());
        const month = pad(d.getMonth() + 1);
        const year = d.getFullYear();

        const hours = pad(d.getHours());
        const minutes = pad(d.getMinutes());
        const seconds = pad(d.getSeconds());

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <h2>Announcements</h2>

                {

                    !showForm && perms.add &&

                    <button
                        type="button"
                        className="add-btn"
                        onClick={openAdd}
                    >

                        <FaPlus />

                        Add Announcement

                    </button>

                }

            </div>

            {

                showForm ? (

                    // ===========================================
                    // INLINE FORM -- shown instead of the table.
                    // ===========================================
                    <div className="inline-form-container">

                        <div className="inline-form-header">

                            <button
                                type="button"
                                className="back-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >

                                <FaArrowLeft />
                                Back to list

                            </button>

                            <h3>
                                {isEdit ? "Update Announcement" : "Add Announcement"}
                            </h3>

                        </div>

                        <div className="inline-form-grid">

                            <textarea
                                name="message"
                                placeholder="Announcement message"
                                value={form.message}
                                onChange={handleChange}
                                disabled={isSaving}
                                rows={4}
                                className="span-2"
                            />

                            <div className="field-block">

                                <label className="field-label">Severity</label>

                                <select
                                    name="severity"
                                    value={form.severity}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                >

                                    {

                                        SEVERITY_OPTIONS.map(option => (

                                            <option key={option} value={option}>
                                                {option.charAt(0).toUpperCase() + option.slice(1)}
                                            </option>

                                        ))

                                    }

                                </select>

                            </div>

                            <div className="field-block">

                                <label className="field-label">Expires At (optional)</label>

                                <input
                                    type="datetime-local"
                                    name="expires_at"
                                    value={form.expires_at}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                />

                            </div>

                            <label className="toggle-row">

                                <span
                                    className={`toggle-switch ${form.is_active ? "on" : ""}`}
                                    onClick={() => !isSaving && handleActiveToggle()}
                                >
                                    <span className="toggle-knob" />
                                </span>

                                <span>
                                    {form.is_active ? "Active" : "Inactive"}
                                </span>

                            </label>

                        </div>

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="save-btn"
                                onClick={saveAnnouncement}
                                disabled={isSaving}
                            >

                                {

                                    isSaving

                                        ? <><FaSpinner className="spin" /> Saving...</>

                                        : isEdit
                                            ? "Update Announcement"
                                            : "Save Announcement"

                                }

                            </button>

                        </div>

                    </div>

                ) : (

                    // ===========================================
                    // TABLE VIEW
                    // ===========================================
                    <div className="table-container">

                        <table>

                            <thead>

                                <tr>

                                    <th>Message</th>
                                    <th style={{ width: "110px" }}>Severity</th>
                                    <th style={{ width: "90px" }}>Status</th>
                                    <th style={{ width: "170px" }}>Expires At</th>
                                    <th style={{ width: "120px" }}>Action</th>

                                </tr>

                            </thead>

                            <tbody>

                                {

                                    isLoading ?

                                        <tr>

                                            <td colSpan="5" style={{ textAlign: "center", padding: "40px" }}>
                                                <FaSpinner className="spin" /> Loading announcements...
                                            </td>

                                        </tr>

                                        :

                                        announcements.length === 0 ?

                                            <tr>

                                                <td colSpan="5" style={{ textAlign: "center", padding: "40px" }}>
                                                    No Records Found
                                                </td>

                                            </tr>

                                            :

                                            announcements.map(item => (

                                                <tr key={item.id}>

                                                    <td className="message-cell">{item.message}</td>

                                                    <td>

                                                        <span className={`severity-badge ${item.severity}`}>
                                                            {item.severity}
                                                        </span>

                                                    </td>

                                                    <td>

                                                        <span className={`status-badge ${item.is_active ? "active" : "inactive"}`}>
                                                            {item.is_active ? "Active" : "Inactive"}
                                                        </span>

                                                    </td>

                                                    <td>{formatDate(item.expires_at)}</td>

                                                    <td>

                                                        <div className="action-buttons">

                                                            {perms.edit &&
                                                            <button
                                                                type="button"
                                                                className="icon-btn edit-btn"
                                                                title="Edit announcement"
                                                                aria-label="Edit announcement"
                                                                onClick={() => openEdit(item)}
                                                                disabled={deletingId === item.id}
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            }

                                                            {perms.delete &&
                                                            <button
                                                                type="button"
                                                                className="icon-btn delete-btn"
                                                                title="Delete announcement"
                                                                aria-label="Delete announcement"
                                                                onClick={() => deleteAnnouncement(item.id)}
                                                                disabled={deletingId === item.id}
                                                            >

                                                                {

                                                                    deletingId === item.id

                                                                        ? <FaSpinner className="spin" />

                                                                        : <FaTrash />

                                                                }

                                                            </button>
                                                            }

                                                        </div>

                                                    </td>

                                                </tr>

                                            ))

                                }

                            </tbody>

                        </table>

                    </div>

                )

            }

        </div>

    );

}

export default Announcements;